import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowRight, Check, MapPin, Phone, ShieldCheck, Clock, Euro, Lightbulb, FileText } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { localServiceBySlug, localServices } from "@/data/local-services";
import {
  locationBySlug,
  locationsByBundesland,
  wienBezirke,
} from "@/data/locations";
import { cityMeta } from "@/data/location-meta";
import { generateLocalContent } from "@/lib/local-content";
import team from "@/assets/schober/team.webp";

export const Route = createFileRoute("/leistungen_/$service_/$ort")({
  loader: ({ params }) => {
    const service = localServiceBySlug[params.service];
    const location = locationBySlug[params.ort];
    if (!service || !location) throw notFound();
    const content = generateLocalContent(service, location);
    return { service, location, content };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return { meta: [] };
    const { content, service, location } = loaderData;
    const url = `https://schober-transportservice.at/leistungen/${service.slug}/${location.slug}`;
    const meta: { title?: string; name?: string; property?: string; content?: string }[] = [
      { title: content.title },
      { name: "description", content: content.metaDescription },
      { name: "keywords", content: `${service.slug}, ${location.slug}, ${service.title}, ${location.name}, ${service.titleLong}, ${location.bundesland}, Schober Transportservice` },
      { property: "og:title", content: content.ogTitle },
      { property: "og:description", content: content.ogDescription },
      { property: "og:image", content: "https://schober-transportservice.at" + service.image },
      { property: "og:type", content: "website" },
      { property: "og:url", content: url },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: content.ogTitle },
      { name: "twitter:description", content: content.ogDescription },
      { name: "twitter:image", content: "https://schober-transportservice.at" + service.image },
    ];
    if (content.noindex) meta.push({ name: "robots", content: "noindex, follow" });
    return {
      meta,
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: LocalServicePage,
  notFoundComponent: () => (
    <div className="container mx-auto max-w-3xl px-4 py-20 text-center">
      <h1 className="font-display text-3xl font-bold">Seite nicht gefunden</h1>
      <p className="mt-4">
        <Link to="/leistungen" className="text-brand-orange underline">
          Zurück zu unseren Leistungen
        </Link>
      </p>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="container mx-auto max-w-3xl px-4 py-20 text-center">
      <h1 className="font-display text-3xl font-bold">Fehler</h1>
      <p className="mt-4 text-muted-foreground">{error instanceof Error ? error.message : String(error)}</p>
    </div>
  ),
});

function LocalServicePage() {
  const { service, location, content } = Route.useLoaderData();
  const meta = cityMeta[location.slug];

  // Verwandte Orte: bevorzugt kuratierte Nachbarn, sonst Bundesland-Geschwister
  const nachbarnSlugs = meta?.nachbarn ?? [];
  const nachbarn = nachbarnSlugs.map((s) => locationBySlug[s]).filter(Boolean);
  const regionGeschwister =
    location.type === "stadt"
      ? locationsByBundesland(location.bundeslandSlug).filter((l) => l.slug !== location.slug)
      : location.type === "wien-bezirk"
        ? wienBezirke.filter((l) => l.slug !== location.slug)
        : location.bundeslandSlug === "wien"
          ? wienBezirke
          : locationsByBundesland(location.bundeslandSlug);
  const related = [...nachbarn, ...regionGeschwister.filter((l) => !nachbarnSlugs.includes(l.slug))].slice(0, 10);

  const otherServices = localServices.filter((s) => s.slug !== service.slug);

  // Schema.org JSON-LD
  const baseUrl = "https://schober-transportservice.at";
  const pageUrl = `${baseUrl}/leistungen/${service.slug}/${location.slug}`;
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "MovingCompany",
        "@id": pageUrl + "#business",
        name: "Schober Transportservice",
        description:
          "Transportunternehmen (Gewerbe Entrümpler). Wir räumen und transportieren das Räumgut ab; Sperrgut wird befugten Entsorgungsbetrieben zur ordnungsgemäßen Entsorgung zugeführt.",
        image: baseUrl + service.image,
        telephone: "+43 680 230 87 00",
        priceRange: "€€",
        url: pageUrl,
        areaServed: { "@type": "Place", name: location.name },
        address: {
          "@type": "PostalAddress",
          addressCountry: "AT",
          addressRegion: location.bundesland,
          addressLocality: location.name,
          postalCode: location.plz ?? undefined,
        },
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "5.0",
          reviewCount: "16",
          bestRating: "5",
          worstRating: "1",
        },
      },
      {
        "@type": "Service",
        serviceType: service.titleLong,
        provider: { "@id": pageUrl + "#business" },
        areaServed: { "@type": "Place", name: location.name },
        name: `${service.titleLong} ${location.name}`,
        description: content.metaDescription,
      },
      {
        "@type": "FAQPage",
        mainEntity: content.faq.map((f: { question: string; answer: string }) => ({
          "@type": "Question",
          name: f.question,
          acceptedAnswer: { "@type": "Answer", text: f.answer },
        })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: baseUrl },
          { "@type": "ListItem", position: 2, name: "Leistungen", item: baseUrl + "/leistungen" },
          { "@type": "ListItem", position: 3, name: service.title, item: `${baseUrl}/leistungen/${service.slug}` },
          { "@type": "ListItem", position: 4, name: location.name, item: pageUrl },
        ],
      },
    ],
  };

  const ortPrep = location.type === "wien-bezirk" ? "im" : "in";

  return (
    <>
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <img
          src={service.image}
          alt={`${service.titleLong} ${location.name} – Schober Transportservice`}
          width={1600}
          height={900}
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-brand-navy/70" aria-hidden />
        <div className="relative z-10 container mx-auto max-w-6xl px-4 py-20 md:py-28 text-white">
          <nav aria-label="Breadcrumb" className="mb-4 text-sm text-white/80">
            <Link to="/" className="hover:text-brand-orange-on-dark">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/leistungen" className="hover:text-brand-orange-on-dark">Leistungen</Link>
            <span className="mx-2">/</span>
            <Link to="/leistungen/$service" params={{ service: service.slug }} className="hover:text-brand-orange-on-dark">
              {service.title}
            </Link>
            <span className="mx-2">/</span>
            <span className="text-white">{location.name}</span>
          </nav>
          <h1 className="font-display text-4xl font-extrabold uppercase tracking-tight md:text-6xl">
            {content.h1}
          </h1>
          <p className="mt-6 max-w-3xl text-lg text-white/90 md:text-xl">{service.shortDescription}</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a
              href="tel:00436802308700"
              className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-6 py-3 font-bold text-brand-orange-foreground transition hover:scale-105"
            >
              <Phone className="h-4 w-4" /> +43 680 230 87 00
            </a>
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 rounded-full border-2 border-white px-6 py-3 font-bold text-white transition hover:bg-white hover:text-brand-navy"
            >
              Kostenloses Angebot <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Intro – 2 Absätze + Sidebar */}
      <section className="container mx-auto max-w-6xl px-4 py-16 md:py-20">
        <div className="grid gap-12 md:grid-cols-3">
          <article className="md:col-span-2 space-y-6">
            <p className="text-lg leading-relaxed text-foreground/90">{content.intro}</p>
            <p className="text-base leading-relaxed text-foreground/80">{content.introExtra}</p>
            <p className="text-base leading-relaxed text-foreground/80">{content.localContext}</p>
            {content.neighborhoodSection && (
              <p className="text-base leading-relaxed text-foreground/80">{content.neighborhoodSection}</p>
            )}
          </article>
          <aside className="rounded-lg bg-muted p-6 h-fit sticky top-24">
            <h2 className="font-display text-xl font-bold text-brand-navy">
              <MapPin className="inline h-5 w-5 text-brand-orange" /> Einsatzgebiet
            </h2>
            <dl className="mt-4 space-y-3 text-sm">
              <div>
                <dt className="font-semibold">Ort:</dt>
                <dd>{location.name}</dd>
              </div>
              <div>
                <dt className="font-semibold">Bundesland:</dt>
                <dd>{location.bundesland}</dd>
              </div>
              {location.plz && (
                <div>
                  <dt className="font-semibold">PLZ:</dt>
                  <dd>{location.plz}</dd>
                </div>
              )}
              {location.region && (
                <div>
                  <dt className="font-semibold">Region:</dt>
                  <dd>{location.region}</dd>
                </div>
              )}
              {location.population && (
                <div>
                  <dt className="font-semibold">Einwohner:</dt>
                  <dd>{location.population.toLocaleString("de-AT")}</dd>
                </div>
              )}
              <div className="pt-3 border-t border-border">
                <dt className="font-semibold">Telefon:</dt>
                <dd>
                  <a href="tel:00436802308700" className="text-brand-orange hover:underline">
                    +43 680 230 87 00
                  </a>
                </dd>
              </div>
              <div className="pt-3 border-t border-border">
                <Link to="/kontakt" className="inline-flex items-center gap-1 font-semibold text-brand-orange hover:underline">
                  Kostenloses Angebot <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            </dl>
          </aside>
        </div>
      </section>

      {/* Vorteile */}
      <section className="bg-muted py-16 md:py-20">
        <div className="container mx-auto max-w-6xl px-4">
          <h2 className="font-display text-3xl font-bold text-brand-navy md:text-4xl">
            Ihre Vorteile mit Schober Transportservice {ortPrep} {location.name}
          </h2>
          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[...service.bullets, ...content.whyUs].slice(0, 9).map((b, i) => (
              <div key={i} className="flex gap-3 rounded-lg bg-background p-5 shadow-sm">
                <Check className="mt-0.5 h-5 w-5 shrink-0 text-brand-orange" />
                <p className="text-sm text-foreground/85">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ablauf */}
      <section className="container mx-auto max-w-6xl px-4 py-16 md:py-20">
        <h2 className="font-display text-3xl font-bold text-brand-navy md:text-4xl">
          So läuft Ihre {service.title} {ortPrep} {location.name} ab
        </h2>
        <ol className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {service.process.map((step: { title: string; text: string }, i: number) => (
            <li key={i} className="rounded-lg border border-border p-6">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-orange font-bold text-brand-orange-foreground">
                {i + 1}
              </div>
              <h3 className="mt-4 font-display text-lg font-bold text-brand-navy">{step.title}</h3>
              <p className="mt-2 text-sm text-foreground/80">{step.text}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* Termin-CTA mit Team-Bild */}
      <section className="bg-blue-950 container-fluid mx-auto">
        <div className="grid lg:grid-cols-2 lg:items-center mx-auto gap-0 min-h-[600px]">
          <div className="p-10 md:p-0 mx-5 md:mx-20">
            <h2 className="font-display text-3xl font-extrabold text-white md:text-4xl">
              Vereinbaren Sie gleich einen Termin für eine Besichtigung {ortPrep} {location.name}!
            </h2>
            <p className="mt-5 leading-relaxed text-white/90">
              Kostenlos &amp; unverbindlich – wir kommen vorbei, sehen uns alles an und erstellen Ihnen
              ein ehrliches Angebot ohne versteckte Kosten. Versichert bis 25.000 €.
            </p>
            <a
              href="tel:+436802308700"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand-orange px-8 py-3.5 text-sm font-bold uppercase tracking-wider text-brand-orange-foreground shadow-lg transition-all hover:scale-105"
            >
              <Phone className="h-4 w-4" /> Jetzt anrufen: 0680 230 87 00
            </a>
          </div>
          <div
            className="overflow-hidden bg-cover bg-center h-[300px] md:h-full"
            style={{ backgroundImage: `url(${team})` }}
            aria-label="Patric Schober – Schober Transportservice"
          />
        </div>
      </section>

      {/* Lokale Tipps */}
      <section className="container mx-auto max-w-6xl px-4 py-16 md:py-20">
        <h2 className="font-display text-3xl font-bold text-brand-navy md:text-4xl">
          <Lightbulb className="inline h-7 w-7 text-brand-orange" /> Worauf Sie {ortPrep} {location.name} achten sollten
        </h2>
        <ul className="mt-8 grid gap-4 md:grid-cols-2">
          {content.localTips.map((tip: string, i: number) => (
            <li key={i} className="flex gap-3 rounded-lg border border-border bg-background p-5">
              <Check className="mt-0.5 h-5 w-5 shrink-0 text-brand-orange" />
              <p className="text-sm text-foreground/85">{tip}</p>
            </li>
          ))}
        </ul>
      </section>

      {/* Fallbeispiele */}
      <section className="bg-muted py-16 md:py-20">
        <div className="container mx-auto max-w-6xl px-4">
          <h2 className="font-display text-3xl font-bold text-brand-navy md:text-4xl">
            <FileText className="inline h-7 w-7 text-brand-orange" /> Typische Aufträge {ortPrep} {location.name}
          </h2>
          <p className="mt-3 max-w-3xl text-foreground/80">
            Anonymisierte Beispiele aus unserer täglichen Arbeit – damit Sie einschätzen können, wie wir Ihre {service.title} planen würden.
          </p>
          <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {content.exampleCases.map((ex: { title: string; text: string }, i: number) => (
              <article key={i} className="rounded-lg bg-background p-6 shadow-sm">
                <h3 className="font-display text-lg font-bold text-brand-navy">{ex.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-foreground/80">{ex.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="bg-brand-navy text-white py-10">
        <div className="container mx-auto max-w-6xl px-4 grid gap-6 md:grid-cols-3 text-center">
          <div className="flex flex-col items-center gap-2">
            <ShieldCheck className="h-8 w-8 text-brand-orange-on-dark" />
            <p className="font-semibold">Versichert bis 25.000 €</p>
          </div>
          <div className="flex flex-col items-center gap-2">
            <Clock className="h-8 w-8 text-brand-orange-on-dark" />
            <p className="font-semibold">Termin in 48 Stunden</p>
          </div>
          <div className="flex flex-col items-center gap-2">
            <Check className="h-8 w-8 text-brand-orange-on-dark" />
            <p className="font-semibold">Angebot – garantiert</p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="container mx-auto max-w-4xl px-4 py-16 md:py-20">
        <h2 className="font-display text-3xl font-bold text-brand-navy md:text-4xl">
          Häufige Fragen zu {service.title} {ortPrep} {location.name}
        </h2>
        <Accordion type="single" collapsible className="mt-8">
          {content.faq.map((f: { question: string; answer: string }, i: number) => (
            <AccordionItem key={i} value={`item-${i}`}>
              <AccordionTrigger className="text-left font-display text-lg font-semibold">
                {f.question}
              </AccordionTrigger>
              <AccordionContent className="text-base leading-relaxed text-foreground/85">
                {f.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Verwandte Orte */}
      {related.length > 0 && (
        <section className="bg-muted py-16">
          <div className="container mx-auto max-w-6xl px-4">
            <h2 className="font-display text-2xl font-bold text-brand-navy md:text-3xl">
              {service.title} auch in der Umgebung
            </h2>
            <ul className="mt-6 flex flex-wrap gap-2">
              {related.map((l) => (
                <li key={l.slug}>
                  <Link
                    to="/leistungen/$service/$ort"
                    params={{ service: service.slug, ort: l.slug }}
                    className="inline-block rounded-full border border-border bg-background px-4 py-2 text-sm transition hover:border-brand-orange hover:text-brand-orange"
                  >
                    {service.title} {l.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Weitere Services */}
      <section className="container mx-auto max-w-6xl px-4 py-16">
        <h2 className="font-display text-2xl font-bold text-brand-navy md:text-3xl">
          Weitere Leistungen {ortPrep} {location.name}
        </h2>
        <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {otherServices.map((s) => (
            <Link
              key={s.slug}
              to="/leistungen/$service/$ort"
              params={{ service: s.slug, ort: location.slug }}
              className="group block rounded-lg border border-border p-5 transition hover:border-brand-orange hover:shadow-md"
            >
              <h3 className="font-display text-lg font-bold text-brand-navy group-hover:text-brand-orange">
                {s.title} {location.name}
              </h3>
              <p className="mt-2 text-sm text-foreground/75 line-clamp-2">{s.shortDescription}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-brand-orange py-16">
        <div className="container mx-auto max-w-4xl px-4 text-center text-brand-orange-foreground">
          <h2 className="font-display text-3xl font-extrabold md:text-4xl">
            Bereit für Ihre {service.title} {ortPrep} {location.name}?
          </h2>
          <p className="mt-4 text-lg">
            Fordern Sie jetzt Ihr kostenlose Beratung an – wir melden uns innerhalb von 24 Stunden.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <a
              href="tel:00436802308700"
              className="inline-flex items-center gap-2 rounded-full bg-brand-navy px-8 py-3.5 font-bold text-white transition hover:scale-105"
            >
              <Phone className="h-4 w-4" /> Jetzt anrufen
            </a>
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 font-bold text-brand-navy transition hover:scale-105"
            >
              Online anfragen <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
